/**
 * 实时同步契约 —— 归一化事件面（**L 域 L2** · `DR-018` §3.2）
 *
 * 依据：规格 **§16**（实时同步 · 推送范围收窄为 4 类 + 并发冲突策略 + 小程序 P0 不接长连接）·
 * L 域 PRD **§5.2**（口径 1–7）· **§7.2**（`AC-L2.1`–`L2.10`）·
 * 架构 **§7.1**（拓扑与鉴权 · 按域推送 · 单一出口）· **§7.2**（客户端呈现策略）·
 * **§7.3**（小程序一致性机制）· **§10.2**（TanStack Query 缓存失效模型配合 WS 增量刷新）。
 *
 * ## 🔴 为什么事件名必须放在共享层（而不是服务端各写一份）
 *
 * `AC-L2.2` 是**必测**的「事件名清单审计」，判据是「**仅 4 类，无第 5 类**」。
 * 若服务端在 `gateway.ts` 里写一份、端侧在 `useRealtime.ts` 里再写一份字面量，
 * 那么"第 5 类"可以**合法地**在任一侧出现而审计看不出来 —— 审计就变成了形式。
 * ⇒ 事件名集合**只有这里一处**：服务端 `emit()` 的入参类型取自本文件，
 * 端侧的订阅与分派也取自本文件；`REALTIME_EVENTS` 的长度即"类数"的判据。
 *
 * ## 🔴 4 类的**归属**（谁发）与**第 4 类的特殊情况**
 *
 * | 事件 | 语义 | 发射方 | 现状 |
 * |---|---|---|---|
 * | {@link RealtimeEvent.Txn} | ① 流水增改删 | C 域 `journal` | ✅ 已接线 |
 * | {@link RealtimeEvent.Account} | ② 账户增改删 + 转账 | B 域 `account` | ✅ 已接线 |
 * | {@link RealtimeEvent.Member} | ④ 成员变动 | L 域 `ledger` | ✅ 已接线 |
 * | {@link RealtimeEvent.Budget} | ③ 预算状态变更 | **H 域提醒中心**（**不是** G 域预算） | ⚠️ **只声明、未接线** |
 *
 * ⚠️ **③ 的发射方不是预算域** —— 规格 §5 原文：「预算域**只算档位并落快照**（`safe`/`warn`/`over`），
 * **不推送、不发事件**；**跨档判定与推送由提醒中心负责**（拉取快照比对，**仅档位升级**触发）」，
 * 且规格 §24 明确**不采纳** v1 的 `budget:alert` 事件模式（该事件在 v1 全仓**无任何消费方**，属死信）。
 * ⇒ 事件**类型**属于这 4 类闭集（`AC-L2.2` 要求恰好 4），但**发射点是 H 域**；
 * H 域未落地前本事件**没有发射方**，这是**已登记的空缺**，不是遗漏。
 *
 * ## 🔴 房间与鉴权（架构 §7.1）
 *
 * ```
 * Web 客户端 ──ws──▶ socket.io（与 HTTP server 共用端口）
 *                      ↓ 握手携带 JWT
 *                  校验 JWT → 解析 user → 校验 ledger_members 成员身份
 *                      ↓ 通过才入房
 *                  房间 = ledger_<ledgerId>          ★ 防越权订阅
 * ```
 *
 * 🔴 **未认证 / 非成员 ⇒ 不拒绝底层连接，只是不入房**（`AC-L2.4`）——
 * 「连不上」与「连上了但收不到」对用户是两种不同的失败，前者会让端侧重试风暴、
 * 后者让端侧安静降级为"轮询/手动刷新"。房间名由 {@link ledgerRoom} 单点构造。
 */

import { z } from 'zod';

// ═══════════════════════ ① 事件名闭集（恰好 4 类） ═══════════════════════

/**
 * 实时事件名 —— 🔴 **闭集，恰好 4 类**（`AC-L2.2`）。
 *
 * 值是 socket.io 的事件名（客户端 `socket.on(<值>)`）。用**名词**而不是动词短语，
 * 是因为"动作"（created / updated / deleted）属于 {@link RealtimeAction}，
 * 放在载荷里 —— 这样新增一个"动作"不会让"类数"从 4 变成 5（类的边界由本枚举钉死）。
 */
export const RealtimeEvent = {
  /** ① 流水增改删（含跨币种转账 —— 转账也是一笔交易） */
  Txn: 'txn',
  /** ② 账户增改删（转账会连带改变两个账户的余额，故账户类自带"转账"语义） */
  Account: 'account',
  /** ③ 预算状态变更 —— ⚠️ **发射方 = H 域提醒中心**（规格 §5；见文件头表格） */
  Budget: 'budget',
  /** ④ 成员变动（加入 / 退出 / 被移除 / 转让所有权） */
  Member: 'member',
} as const;
export type RealtimeEvent = (typeof RealtimeEvent)[keyof typeof RealtimeEvent];

/**
 * 全部事件名（**顺序即枚举声明顺序**）。
 *
 * 🔴 `AC-L2.2` 的审计直接断言 **`REALTIME_EVENTS.length === 4`** 且集合精确相等 ——
 * 这比"人眼过一遍事件名"强：任何人想加第 5 类，都会让这条断言红。
 */
export const REALTIME_EVENTS = [
  RealtimeEvent.Txn,
  RealtimeEvent.Account,
  RealtimeEvent.Budget,
  RealtimeEvent.Member,
] as const;

/** zod 用的事件名枚举（供将来 WS 载荷校验 / 端侧白名单过滤复用） */
export const RealtimeEventSchema = z.enum(REALTIME_EVENTS);

/**
 * 房间名构造 —— 🔴 **唯一实现**（`ledger_<ledgerId>`）。
 *
 * 服务端 `join` / `to().emit()` 与端侧的调试日志都走它 ⇒ 前缀一旦写错（如 `ledger-`）
 * 不会出现"服务端发到 A 房间、端侧订阅 B 房间"这种**两边都不报错**的静默失配。
 */
export function ledgerRoom(ledgerId: string): string {
  return `ledger_${ledgerId}`;
}

// ═══════════════════════ ② 载荷 ═══════════════════════

/** 载荷共有的三个字段 —— 每类事件都带，端侧据此做**陈旧丢弃**与提示文案 */
export interface RealtimeBasePayload {
  /**
   * 事件所属账本。
   *
   * 🔴 房间已经限定了账本，**为什么还要带**：用户切账本后，上一本账本的
   * 在途事件可能**晚到**（socket 已重连到新房间，但旧房间的包还在路上）。
   * 端侧凭本字段**丢弃不同账本的事件** —— 否则会把 A 账本的流水插进 B 账本的列表，
   * 而这是"看起来像数据错乱"的静默缺陷（架构 §10.2「账本分键 · 切换账本清缓存」）。
   */
  ledgerId: string;
  /**
   * 触发者（`users.id`）—— 端侧据此**忽略自己刚做的操作**（避免重放自己的撤销/提示）。
   * ⚠️ 注意这是**记录人**，不是归属人（`DR-018` §3.2）。
   */
  actorUserId: string;
  /** 事件产生时刻（UTC 毫秒） */
  at: number;
}

/** 动作取值集 —— 四类事件共用（不新开 `enums/`：这是**传输层取值集**，同 `ContributionWindow` 之例） */
export const RealtimeAction = {
  Created: 'created',
  Updated: 'updated',
  Deleted: 'deleted',
  Restored: 'restored',
} as const;
export type RealtimeAction = (typeof RealtimeAction)[keyof typeof RealtimeAction];

/**
 * ① 流水事件载荷。
 *
 * 🔴 **不带金额、不带分类名等业务字段** —— 端侧收到后**失效对应 query key 重新取数**，
 * 而不是"拿事件载荷拼一行出来"。理由（架构 §10.2）：WS 载荷一旦承载业务字段，
 * 就出现了**第二份行装配实现**（与 `GET /api/transactions` 的那份并列），
 * 两处的字段派生迟早不一致（金额要过三段式支出内核、要 join 账户名 / 分类名）。
 */
export interface RealtimeTxnPayload extends RealtimeBasePayload {
  action: RealtimeAction;
  /** 受影响的交易 id（主行 `transactions.id`） */
  txnId: string;
}

/** ② 账户事件载荷 */
export interface RealtimeAccountPayload extends RealtimeBasePayload {
  action: RealtimeAction;
  /** 受影响的账户 id */
  accountId: string;
  /**
   * 本次是否由**转账**引起（规格 §16 把"账户增改删 + 转账"并作一类）。
   * 端侧可据此决定要不要同时失效流水列表（转账会生成一笔交易）。
   */
  viaTransfer?: boolean;
}

/**
 * ③ 预算状态事件载荷 —— ⚠️ **发射方 = H 域提醒中心**（**只声明、未接线**，见文件头表格）。
 *
 * 之所以现在就把载荷定下来：`AC-L2.2` 要求"恰好 4 类"，事件**名**与**形状**都属于契约；
 * 而 H 域落地时若发现形状不够用，改契约是**正常流程**（比 H 域自己造第 5 类事件好）。
 */
export interface RealtimeBudgetPayload extends RealtimeBasePayload {
  action: RealtimeAction;
  /** 账本本地日历日 `YYYY-MM-DD`（档位是按日算的） */
  day: string;
  /** 变更后的档位 —— 与 `budget_status_snapshot` 的档位同一取值集（由内核定义） */
  status: string;
}

/** ④ 成员事件载荷 */
export interface RealtimeMemberPayload extends RealtimeBasePayload {
  /** 成员的来去用 `Updated` 表达（角色变化 / 转让所有权）；新成员用 `Created` */
  action: RealtimeAction;
  /** 涉及的用户 id */
  userId: string;
}

// ═══════════════════════ ③ 事件 → 载荷 的映射（端侧分派的类型依据） ═══════════════════════

/**
 * 事件名 → 载荷类型。
 *
 * 服务端的广播封装（`RealtimeService.emit`）与端侧的订阅回调都以此为类型依据 ⇒
 * **事件名与载荷形状的对应关系只有这一处**，写错事件名即编译错误。
 */
export interface RealtimeEventMap {
  [RealtimeEvent.Txn]: RealtimeTxnPayload;
  [RealtimeEvent.Account]: RealtimeAccountPayload;
  [RealtimeEvent.Budget]: RealtimeBudgetPayload;
  [RealtimeEvent.Member]: RealtimeMemberPayload;
}
